import { DurationSeconds } from 'lib/datetime/durationInSeconds'
import * as isomorphicLocalStorage from 'lib/storage/isomorphicLocalStorage'
import * as isomorphicSessionStorage from 'lib/storage/isomorphicSessionStorage'

export const SEEN_EXPIRING_VOUCHERS_STORAGE_KEY = 'expiringVoucherReminderSeen'
export const REMINDED_THIS_SESSION_STORAGE_KEY = 'expiringVoucherReminderShown'

/**
 * Longer than the 48-hour reminder window, so a voucher cannot be reminded about twice.
 */
const SEEN_VOUCHER_TTL_MS = DurationSeconds.THREE_DAYS * 1000

/** Voucher id to the time (ms) after which it no longer needs remembering. */
type SeenVouchers = Record<string, number>

function readSeenVouchers(now: number): SeenVouchers {
  const stored: unknown = isomorphicLocalStorage.get(SEEN_EXPIRING_VOUCHERS_STORAGE_KEY)
  if (!stored || typeof stored !== 'object') return {}

  return Object.fromEntries(
    Object.entries(stored).filter(
      (entry): entry is [string, number] => typeof entry[1] === 'number' && entry[1] > now,
    ),
  )
}

/** Ids of vouchers the expiring voucher reminder has already shown on this device (GR-5151). */
export function getSeenExpiringVoucherIds(): Array<string> {
  if (typeof window === 'undefined') return []
  return Object.keys(readSeenVouchers(Date.now()))
}

/** True once any expiring voucher reminder has shown in this browser session (GR-5151). */
export function hasShownExpiringVoucherReminderThisSession(): boolean {
  if (typeof window === 'undefined') return false
  return isomorphicSessionStorage.get(REMINDED_THIS_SESSION_STORAGE_KEY) === true
}

/** Records the shown voucher on this device, and that this session has had its one reminder. */
export function markExpiringVoucherSeen(voucherId: string): void {
  const now = Date.now()
  // Rewriting only the unexpired ids keeps the stored list from growing.
  isomorphicLocalStorage.set(SEEN_EXPIRING_VOUCHERS_STORAGE_KEY, {
    ...readSeenVouchers(now),
    [voucherId]: now + SEEN_VOUCHER_TTL_MS,
  } satisfies SeenVouchers)
  isomorphicSessionStorage.set(REMINDED_THIS_SESSION_STORAGE_KEY, true)
}
